import { FormLayout } from '@expressable/syntax-ds';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useBoolean } from 'react-use';
import { LoginFormData } from '.';
import { useCognitoUserContext } from '../AWSCognitoWrapper/context';
import { LoginFormUi } from './formUi';
import { getLocalizedSchema } from './schema';
import { FC } from 'react';

type Props = {
  className?: string;
};

export const LoginForm: FC<Props> = ({ className }): JSX.Element => {
  const cognitoCtx = useCognitoUserContext();
  if (cognitoCtx.isLoggedIn) {
    throw new Error(`<Login/> component shouldn't render if there's an active session`);
  }

  const { logIn: onLogIn } = cognitoCtx;

  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useBoolean(false);

  const methods = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(getLocalizedSchema(t)),
    disabled: isLoading,
  });

  const { handleSubmit, setError } = methods;

  const onSubmit = async ({ email, password }: LoginFormData) => {
    setIsLoading(true);

    try {
      await onLogIn(email, password);
    } catch {
      setError('root', {
        message: t('login.form.error'),
      });
    }

    setIsLoading(false);
  };

  return (
    <FormProvider {...methods}>
      <FormLayout onSubmit={handleSubmit(onSubmit)} className={className}>
        <LoginFormUi isLoading={isLoading} />
      </FormLayout>
    </FormProvider>
  );
};
